import React, { useEffect, useState } from "react";

import Heading from "@busbud/design-system-components/dist/Heading";
import Stack from "@busbud/design-system-components/dist/Stack";
import Text from "@busbud/design-system-components/dist/Text";

import { Markdown } from "@app/components/markdown";

import { useStyles } from "./carousel.styles";

const CAROUSEL_SPEED = 5000; // slide every 5sec

export type CarouselItem = {
  illustration_src: string;
  title: string;
  text: string;
};

interface Props {
  items: CarouselItem[];
  start_index?: number;
}

export const Carousel: React.FC<Props> = ({ items, start_index = 0 }) => {
  const { cx, classes } = useStyles();
  const [current_item_index, setCurrentItemIndex] = useState(
    Math.min(start_index, items.length - 1)
  );
  // @ts-expect-error TS2339 Unsafe indexed access (noUncheckedIndexedAccess); tracked in #24591
  const { illustration_src, title, text } = items[current_item_index];

  useEffect(() => {
    setCurrentItemIndex(Math.min(start_index, items.length - 1));
  }, [start_index, items]);

  useEffect(() => {
    // Preload images at mount to avoid the loading glitch when sliding
    const imagesPreload = items.map(({ illustration_src }) => illustration_src);
    imagesPreload.forEach(image => {
      const newImage = new Image();
      newImage.src = image;
    });
  }, [items]);

  useEffect(() => {
    const timer = setInterval(() => {
      setCurrentItemIndex(index =>
        index === items.length - 1 ? 0 : index + 1
      );
    }, CAROUSEL_SPEED);
    return () => clearInterval(timer);
  }, [current_item_index, items]);

  return (
    <Stack
      direction="column"
      alignItems="center"
      spacing={2}
      className={classes.container}
    >
      <Stack
        direction="column"
        alignItems="center"
        justifyContent="center"
        spacing={1}
        className={classes.item}
        key={current_item_index}
      >
        {/* eslint-disable-next-line jsx-a11y/alt-text -- TODO: [fix-jsx-a11y] */}
        <img src={illustration_src} />
        <Heading size="md">
          <Markdown source={title} />
        </Heading>
        <Text size="lg" component="span" color="text.secondary.opaque">
          <Markdown source={text} />
        </Text>
      </Stack>

      <Stack spacing={1}>
        {items.map((_, index) => (
          /* eslint-disable-next-line
             jsx-a11y/no-static-element-interactions,
             jsx-a11y/click-events-have-key-events
             -- TODO: [fix-jsx-a11y] */
          <div
            onClick={() => setCurrentItemIndex(index)}
            className={cx(classes.dot, {
              [classes.selected]: index === current_item_index
            })}
            key={index}
          />
        ))}
      </Stack>
    </Stack>
  );
};
